// lib/scroll-depth.ts
// Pure logic behind the GA4 `scroll_depth` event (Priority 292). No DOM, no
// network — components/public/ScrollDepthTracker.tsx owns the scroll listener
// and the gtag() call; everything decidable without a browser lives here so
// it is unit-testable (tests/unit/scroll-depth.test.ts).

export const SCROLL_DEPTH_EVENT = "scroll_depth";

/** The `percent` values the event may carry, ascending. */
export const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 90] as const;

/** Guides that emit the event: the first N returned by getAllGuides(). */
export const SCROLL_DEPTH_TOP_GUIDE_COUNT = 3;

/**
 * Which thresholds to emit now: every threshold at or below `percent` that
 * is not already in `alreadySent`, ascending. The caller adds what it
 * actually sent to its own set, so each percent fires at most once per page
 * view. A non-finite `percent` (0-height document → NaN) emits nothing.
 */
export function newScrollDepthThresholds(
  percent: number,
  alreadySent: ReadonlySet<number>,
): number[] {
  if (!Number.isFinite(percent)) return [];
  return SCROLL_DEPTH_THRESHOLDS.filter(
    (threshold) => percent >= threshold && !alreadySent.has(threshold),
  );
}

/**
 * True only when the `ff_consent` cookie (components/public/CookieConsent.tsx)
 * is exactly "accepted". Missing, declined, or any other value is false.
 */
export function hasAcceptedCookieConsent(cookieHeader: string | null | undefined): boolean {
  if (!cookieHeader) return false;
  return cookieHeader
    .split(";")
    .some((pair) => pair.trim() === "ff_consent=accepted");
}

/**
 * True when `slug` is one of the first SCROLL_DEPTH_TOP_GUIDE_COUNT guides in
 * `guides` (pass getAllGuides()). Derived from list order rather than a
 * hardcoded slug list, so it follows GUIDES when that is reordered.
 */
export function isScrollDepthGuide(
  slug: string,
  guides: ReadonlyArray<{ slug: string }>,
): boolean {
  return guides
    .slice(0, SCROLL_DEPTH_TOP_GUIDE_COUNT)
    .some((guide) => guide.slug === slug);
}
